import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import {
  MrfiktivXInvoiceImportReportViewModelGen,
  MrfiktivXInvoiceValidationReportViewModelGen
} from "../v1/data-contracts";
import { ContentType } from "../v1/http-client";
import { XInvoice } from "../v1/XInvoice";
import { XInvoiceProfileEnum } from "@/lib/enum/x-invoice-profile.enum";

/**
 * Communicate with the X-Invoice (EN 16931 / XRechnung / ZUGFeRD) API
 */
class XInvoiceService {
  mrfiktivProxy: XInvoice;

  /**
   * Construct service
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.mrfiktivProxy = new XInvoice(mrfiktivHttpClientProvider.client());
  }

  /**
   * Import an X-Invoice file (XRechnung XML or ZUGFeRD PDF).
   *
   * Bypasses the generated `xInvoiceControllerImport` wrapper: swagger-typescript-api drops query
   * params from every multipart/FormData endpoint's typed signature in this codebase, even though
   * `validate`/`force` are correctly declared on the backend. Calling the proxy's public `http`
   * client directly is the supported escape hatch — see the design doc for details.
   *
   * @param partnerId
   * @param file
   * @param options.validate validate and map only: nothing is created or stored
   * @param options.force import even when the external validator rejects the document
   * @param options.sellerCompanyId references the supplier company on the seller party
   */
  async import(
    partnerId: string,
    file: File,
    options: { validate?: boolean; force?: boolean; sellerCompanyId?: string } = {}
  ): Promise<MrfiktivXInvoiceImportReportViewModelGen> {
    const { data } = await this.mrfiktivProxy.http.request<MrfiktivXInvoiceImportReportViewModelGen, any>({
      path: `/partner/${partnerId}/invoice/x-invoice`,
      method: "POST",
      query: { validate: options.validate, force: options.force, sellerCompanyId: options.sellerCompanyId },
      body: { file },
      secure: true,
      type: ContentType.FormData,
      format: "json"
    });

    return data;
  }

  /**
   * Export an outbound invoice as X-Invoice (EN 16931 CII) XML.
   * @param partnerId
   * @param invoiceId
   * @param profile overrides the resolved e-invoice profile
   */
  async exportXml(partnerId: string, invoiceId: string, profile?: XInvoiceProfileEnum) {
    return (await this.mrfiktivProxy.xInvoiceControllerExportXml({ partnerId, invoiceId, profile })).data;
  }

  /**
   * Validate an existing invoice's X-Invoice XML against the external validator.
   * @param partnerId
   * @param invoiceId
   * @param profile overrides the resolved e-invoice profile
   */
  async validate(
    partnerId: string,
    invoiceId: string,
    profile?: XInvoiceProfileEnum
  ): Promise<MrfiktivXInvoiceValidationReportViewModelGen> {
    return (await this.mrfiktivProxy.xInvoiceControllerValidate({ partnerId, invoiceId, profile })).data;
  }

  /**
   * Validate an X-Invoice file (XRechnung XML or ZUGFeRD PDF) against the external validator without
   * importing it.
   * @param partnerId
   * @param file
   */
  async validateFile(partnerId: string, file: File): Promise<MrfiktivXInvoiceValidationReportViewModelGen> {
    return (await this.mrfiktivProxy.xInvoiceControllerValidateFile(partnerId, { file })).data;
  }
}

/**
 * Communicate with the X-Invoice API
 */
export default new XInvoiceService(new MrfiktivHttpClientProvider());
